import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtDT } from "../../lib/constants";
import { mapUnavailableReason } from "../../lib/estimateUnavailable";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { EnvelopeSimple, Phone, MapPin, Package, CircleNotch } from "@phosphor-icons/react";
import AdminNav from "../../components/AdminNav";

const STATUS_CLS = { NEW: "status-warning", CLOSED: "status-success", REJECTED: "status-danger" };
const routeStr = (o, d) => (o && d ? `${o} → ${d}` : null);
const reasonText = (codes) => {
  if (!Array.isArray(codes) || codes.length === 0) return "—";
  return mapUnavailableReason(codes).title || codes.join(", ");
};

function ReviewRequestDrawer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    api.get(`/admin/review-requests/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.displayMessage || "Could not load request"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const act = async (kind) => {
    const reason = window.prompt(`Reason to ${kind} this review request (min 6 chars):`);
    if (!reason || reason.trim().length < 6) return toast.error("Reason must be at least 6 characters");
    setBusy(true);
    try {
      await api.post(`/admin/review-requests/${id}/${kind}`, { reason: reason.trim() });
      toast.success(`Review request ${kind === "close" ? "closed" : "rejected"}`);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.displayMessage || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="review-request-drawer">
        <SheetHeader>
          <SheetTitle className="heading text-xl tracking-tight">{data ? data.public_request_id : "Review request"}</SheetTitle>
          <SheetDescription className="sr-only">Booking review request detail.</SheetDescription>
        </SheetHeader>
        {loading ? (
          <div className="mt-6 text-sm text-[hsl(var(--muted))] flex items-center gap-2"><CircleNotch className="animate-spin" size={14} /> Loading…</div>
        ) : data ? (
          <div className="mt-4 space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`status-pill ${STATUS_CLS[data.status] || "status-info"}`} data-testid="review-request-status">{data.status}</span>
              <span className="text-xs text-[hsl(var(--muted))] ml-auto">{fmtDT(data.created_at)}</span>
            </div>
            <div className="flex items-center gap-1.5" data-testid="review-request-email"><EnvelopeSimple size={13} className="text-[hsl(var(--muted))]" /> {data.requester_email || "—"}</div>
            <div className="flex items-center gap-1.5"><Phone size={13} className="text-[hsl(var(--muted))]" /> {data.requester_phone || "—"}</div>
            <div className="flex items-center gap-1.5"><MapPin size={13} className="text-[hsl(var(--muted))]" /> {routeStr(data.inferred_source_airport, data.inferred_destination_airport) || "—"}</div>
            <div className="text-xs text-[hsl(var(--muted))]">Pickup: {data.pickup_address || data.pickup_pincode || "—"} · Delivery: {data.delivery_address || data.delivery_pincode || "—"}</div>
            {Array.isArray(data.package_lines) && data.package_lines.length > 0 && (
              <div className="flex items-start gap-1.5"><Package size={13} className="text-[hsl(var(--muted))] mt-0.5" /> {data.package_lines.length} package line(s)</div>
            )}
            {(data.goods_category || data.invoice_value != null) && (
              <div className="text-xs text-[hsl(var(--muted))]">Goods: {data.goods_category || "—"}{data.invoice_value != null ? ` · Invoice ₹${data.invoice_value}` : ""}</div>
            )}
            <div>
              <div className="overline mb-1">Reason</div>
              <div>{reasonText(data.reason_codes)}</div>
              {Array.isArray(data.reason_codes) && data.reason_codes.length > 0 && (
                <div className="text-[11px] text-[hsl(var(--muted))] mt-1">Codes: {data.reason_codes.join(", ")}</div>
              )}
            </div>
            {data.reason_summary && (<div><div className="overline mb-1">Sender note</div><div className="text-xs">{data.reason_summary}</div></div>)}
            {data.internal_note && (<div><div className="overline mb-1">Internal note</div><pre className="text-[11px] whitespace-pre-wrap text-[hsl(var(--muted))]">{data.internal_note}</pre></div>)}
            {data.status === "NEW" && (
              <div className="flex gap-2 pt-2">
                <button className="btn-ghost text-xs" disabled={busy} onClick={() => act("close")} data-testid="review-request-close-btn">Mark handled / close</button>
                <button className="btn-ghost text-xs text-[hsl(var(--danger))]" disabled={busy} onClick={() => act("reject")} data-testid="review-request-reject-btn">Reject</button>
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// Admin → Review Requests (Super Admin only). AUTH_BOOKING requests from signed-in Senders.
// Per-request rows, never aggregated.
export default function ReviewRequests() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/review-requests")
      .then((r) => setRows(r.data.review_requests || []))
      .catch((e) => toast.error(e.displayMessage))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!profile) return null;
  if (profile.role !== "SUPER_ADMIN") return <Navigate to="/app" replace />;

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-[1400px] mx-auto p-6" data-testid="review-requests-page">
      <div className="mb-6">
        <div className="overline">SIGNED-IN SENDER REQUESTS</div>
        <h1 className="heading text-3xl">Review Requests</h1>
        <p className="text-sm text-[hsl(var(--muted))] mt-1">Callback / review requests raised by signed-in Senders during booking when a shipment could not be auto-priced. Each request is a separate row.</p>
      </div>

      {loading ? (
        <div className="grid-card p-6 text-sm text-[hsl(var(--muted))]" data-testid="review-requests-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="grid-card p-6 text-sm text-[hsl(var(--muted))]" data-testid="review-requests-empty">No review requests yet.</div>
      ) : (
        <div className="grid-card overflow-hidden" data-testid="review-requests-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[hsl(var(--border))]">
                <th className="px-4 py-3 overline font-normal">Request ID</th>
                <th className="px-4 py-3 overline font-normal">Email</th>
                <th className="px-4 py-3 overline font-normal">Route</th>
                <th className="px-4 py-3 overline font-normal">Reason</th>
                <th className="px-4 py-3 overline font-normal">Status</th>
                <th className="px-4 py-3 overline font-normal">Requested At</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted)/0.06)] cursor-pointer transition-colors"
                    onClick={() => setActive(r.id)} data-testid={`review-request-row-${r.public_request_id}`}>
                  <td className="px-4 py-3"><span className="id-chip">{r.public_request_id}</span></td>
                  <td className="px-4 py-3" data-testid="review-request-email">{r.requester_email || "—"}</td>
                  <td className="px-4 py-3 text-[hsl(var(--muted))]" data-testid="review-request-route">{routeStr(r.inferred_source_airport, r.inferred_destination_airport) || "—"}</td>
                  <td className="px-4 py-3" data-testid="review-request-reason">{reasonText(r.reason_codes)}</td>
                  <td className="px-4 py-3"><span className={`status-pill ${STATUS_CLS[r.status] || "status-info"}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-[hsl(var(--muted))]">{fmtDT(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ReviewRequestDrawer id={active} onClose={() => setActive(null)} onChanged={load} />
    </div>
    </>
  );
}
